/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Quick Schedule Form
 *
 * @file QuickScheduleForm.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { CircularProgress, GlobalStyles } from '@mui/material';
import QuickScheduleDaysDropdown from './QuickScheduleDaysDropdown';
import QuickScheduleDateField from './QuickScheduleDateField';
import QuickScheduleTimeField from './QuickScheduleTimeField';
import QuickScheduleTimezoneField from './QuickScheduleTimezoneField';
import CustomModal from '../common/CustomModal';
import {
  QuickScheduleCancelButton,
  QuickScheduleSaveButton,
  QuickScheduleFormStylesRoot,
  QuickScheduleFormStylesText,
  classes,
  portalSlotStyles
} from './styles/QuickScheduleFormStyles';
import {
  buildFormDefaultValues,
  buildEndDateInRangeValidator,
  buildStartDateInRangeValidator,
  getCourseBoundedEndMaxDate,
  getCourseBoundedEndMinDate,
  getCourseBoundedStartMaxDate,
  getCourseBoundedStartMinDate,
  hasMatchingDueDaysInDateRange,
  isEndDateAfterCourseMax,
  isEndDateBeforeMinimum,
  isStartDateAfterCourseMax,
  isSubmitButtonDisabled,
  toEndDateIso,
  toStartDateIso
} from './quickScheduleFormUtils';

const QuickScheduleForm = ({
  textInfo,
  initialValues,
  daysOfWeek,
  timeZoneOptions,
  parentApp,
  isSubmitting,
  courseDateBounds,
  callback
}) => {
  const formDefaults = buildFormDefaultValues(initialValues, timeZoneOptions, courseDateBounds);
  const [showDateRangeAlert, setShowDateRangeAlert] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { isValid, errors }
  } = useForm({
    defaultValues: formDefaults,
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  useEffect(() => {
    trigger();
  }, [trigger]);

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const dueDays = watch('dueDays');
  const dueTime = watch('dueTime');
  const timeZone = watch('timeZone');

  // Course bounds: start > course start, end < course end (also not before today).
  const startDateMinDate = useMemo(
    () => getCourseBoundedStartMinDate(courseDateBounds),
    [courseDateBounds]
  );
  const startDateMaxDate = useMemo(
    () => getCourseBoundedStartMaxDate(courseDateBounds),
    [courseDateBounds]
  );
  const endDateMinDate = useMemo(
    () => getCourseBoundedEndMinDate(startDate),
    [startDate]
  );
  const endDateMaxDate = useMemo(
    () => getCourseBoundedEndMaxDate(courseDateBounds),
    [courseDateBounds]
  );
  const showTimeZone = timeZoneOptions?.length > 0;

  const watchedFormValues = useMemo(() => ({
    dueDays,
    startDate,
    endDate,
    dueTime,
    timeZone
  }), [dueDays, endDate, startDate, dueTime, timeZone]);

  const hasValidDueDayRange = useMemo(
    () => hasMatchingDueDaysInDateRange(watchedFormValues),
    [watchedFormValues]
  );

  useEffect(() => {
    if (!dueDays?.length || !startDate || !endDate) {
      return;
    }

    if (!hasMatchingDueDaysInDateRange(watchedFormValues)) {
      setShowDateRangeAlert(true);
    }
  }, [dueDays, endDate, startDate, watchedFormValues]);

  const handleStartDateChange = useCallback((_dateStr, pickedDate, newStartIso) => {
    callback?.onStartDateChange?.(pickedDate, newStartIso);

    const currentEndDate = getValues('endDate');

    if (!currentEndDate || !newStartIso) {
      return;
    }

    if (isEndDateBeforeMinimum(currentEndDate, newStartIso)) {
      setValue('endDate', '', { shouldValidate: true, shouldDirty: true });

      return;
    }

    if (isStartDateAfterCourseMax(newStartIso, courseDateBounds)) {
      setValue('endDate', '', { shouldValidate: true, shouldDirty: true });

      return;
    }

    // Clear end date when it falls outside the course-bounded maximum.
    if (isEndDateAfterCourseMax(currentEndDate, courseDateBounds)) {
      setValue('endDate', '', { shouldValidate: true, shouldDirty: true });
    }
  }, [callback, courseDateBounds, getValues, setValue]);

  const handleEndDateChange = useCallback((date, iso) => {
    callback?.onEndDateChange?.(date, iso);
  }, [callback]);

  const handleFormSubmit = (values) => {
    if (!hasMatchingDueDaysInDateRange(values)) {
      setShowDateRangeAlert(true);

      return;
    }

    callback?.onSave?.(values);
  };

  const isSubmitDisabled = isSubmitButtonDisabled({
    isSubmitting,
    isValid,
    hasValidDueDayRange
  });

  const renderHeader = () => (
    <div className={classes.sectionHeader}>
      <h2 className={classes.sectionTitle}>{textInfo.sectionTitle}</h2>
      <p className={classes.sectionDescription}>{textInfo.sectionDescription}</p>
    </div>
  );

  const renderDueTimeSection = () => (
    <div className={classes.dueTimeSection}>
      <div className={classes.dueTimeField}>
        <QuickScheduleTimeField
          name="dueTime"
          control={control}
          label={textInfo.dueTimeLabel}
          parentApp={parentApp}
          rules={{
            validate: (value) => (
              value && String(value).trim() !== ''
                ? true
                : textInfo.dueTimeRequiredError
            )
          }}
          onChange={(value) => callback?.onDueTimeChange?.(value)}
        />
      </div>

      {showTimeZone && (
        <div className={classes.timeZoneField}>
          <QuickScheduleTimezoneField
            name="timeZone"
            control={control}
            label={textInfo.timeZoneLabel}
            options={timeZoneOptions}
            rules={{
              required: textInfo.timeZoneRequiredError
            }}
            onChange={(value) => callback?.onTimeZoneChange?.(value)}
          />
        </div>
      )}
    </div>
  );

  const renderDateRow = () => (
    <div className={classes.dateRow}>
      <div className={classes.dateField}>
        <QuickScheduleDateField
          name="startDate"
          control={control}
          label={textInfo.startDateLabel}
          parentApp={parentApp}
          minDate={startDateMinDate}
          maxDate={startDateMaxDate}
          placeholderText={textInfo.datePlaceholder}
          dateFormat={textInfo.dateFormat}
          rules={{
            required: textInfo.startDateRequiredError,
            validate: buildStartDateInRangeValidator(
              courseDateBounds,
              textInfo.startDateOutOfRangeError
            )
          }}
          toIso={toStartDateIso}
          onDateChange={handleStartDateChange}
        />
      </div>
      <div className={classes.dateField}>
        <QuickScheduleDateField
          name="endDate"
          control={control}
          label={textInfo.endDateLabel}
          parentApp={parentApp}
          minDate={endDateMinDate}
          maxDate={endDateMaxDate}
          placeholderText={textInfo.datePlaceholder}
          dateFormat={textInfo.dateFormat}
          rules={{
            required: textInfo.endDateRequiredError,
            validate: buildEndDateInRangeValidator(
              courseDateBounds,
              textInfo.endDateOutOfRangeError
            )
          }}
          toIso={(pickedDate) => toEndDateIso(pickedDate, startDate)}
          onDateChange={(_dateStr, pickedDate, iso) => handleEndDateChange(pickedDate, iso)}
        />
      </div>
    </div>
  );

  const renderActions = () => (
    <div className={classes.actionsRow}>
      <QuickScheduleCancelButton
        type="button"
        onClick={() => callback?.onCancel?.()}
        disabled={isSubmitting}
      >
        <span>{textInfo.cancelLabel}</span>
      </QuickScheduleCancelButton>
      <QuickScheduleSaveButton
        type="submit"
        disabled={isSubmitDisabled}
      >
        {isSubmitting ? <CircularProgress size={20} color="inherit" /> : <span>{textInfo.saveLabel}</span>}
      </QuickScheduleSaveButton>
    </div>
  );

  return (
    <QuickScheduleFormStylesRoot className={classes.formPane}>
      <GlobalStyles styles={portalSlotStyles} />
      {renderHeader()}

      <form
        className={classes.form}
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
      >
        <div
          className={classes.liveRegion}
          aria-live="polite"
          aria-relevant="all"
        >
          {Object.keys(errors).length > 0 ? textInfo.formValidationLiveRegion : ''}
        </div>

        <div className={classes.formBody}>
          <QuickScheduleDaysDropdown
            name="dueDays"
            control={control}
            id="quick-schedule-due-days"
            label={textInfo.dueDaysLabel}
            helperText={textInfo.dueDaysTip}
            placeholder={textInfo.dueDaysPlaceholder}
            daysOfWeek={daysOfWeek}
            requiredErrorMessage={textInfo.dueDaysRequiredError}
            onChange={(value) => callback?.onDueDaysChange?.(value)}
          />

          {renderDateRow()}
          {renderDueTimeSection()}
        </div>

        {renderActions()}
      </form>

      <CustomModal
        open={showDateRangeAlert}
        onClose={() => setShowDateRangeAlert(false)}
        classes={{
          paper: classes.alertModalPaper,
          content: classes.alertModalContent,
          closeButton: classes.alertModalCloseButton
        }}
      >
        <QuickScheduleFormStylesText component="h2" className={classes.alertModalTitle}>
          {textInfo.noAssignmentsModalTitle}
        </QuickScheduleFormStylesText>
        <QuickScheduleFormStylesText component="p" className={classes.alertModalMessage}>
          {textInfo.noAssignmentsModalMessage}
        </QuickScheduleFormStylesText>
        <QuickScheduleFormStylesRoot className={classes.alertModalFooter}>
          <button
            type="button"
            className={classes.alertModalDoneButton}
            onClick={() => setShowDateRangeAlert(false)}
          >
            {textInfo.noAssignmentsModalDoneLabel}
          </button>
        </QuickScheduleFormStylesRoot>
      </CustomModal>
    </QuickScheduleFormStylesRoot>
  );
};

QuickScheduleForm.propTypes = {
  textInfo: PropTypes.shape({
    pageTitle: PropTypes.string.isRequired,
    sectionTitle: PropTypes.string.isRequired,
    sectionDescription: PropTypes.string.isRequired,
    dueDaysLabel: PropTypes.string.isRequired,
    dueDaysTip: PropTypes.string,
    dueDaysPlaceholder: PropTypes.string,
    dueDaysRequiredError: PropTypes.string.isRequired,
    dueTimeLabel: PropTypes.string.isRequired,
    dueTimeRequiredError: PropTypes.string.isRequired,
    timeZoneLabel: PropTypes.string,
    timeZoneRequiredError: PropTypes.string,
    startDateLabel: PropTypes.string.isRequired,
    endDateLabel: PropTypes.string.isRequired,
    datePlaceholder: PropTypes.string.isRequired,
    dateFormat: PropTypes.string,
    startDateRequiredError: PropTypes.string.isRequired,
    endDateRequiredError: PropTypes.string.isRequired,
    startDateOutOfRangeError: PropTypes.string,
    endDateOutOfRangeError: PropTypes.string,
    cancelLabel: PropTypes.string.isRequired,
    saveLabel: PropTypes.string.isRequired,
    noAssignmentsModalTitle: PropTypes.string,
    noAssignmentsModalMessage: PropTypes.string,
    noAssignmentsModalDoneLabel: PropTypes.string,
    closeLabel: PropTypes.string,
    backButtonLabel: PropTypes.string,
    aiDisclaimer: PropTypes.string,
    formValidationLiveRegion: PropTypes.string
  }).isRequired,
  initialValues: PropTypes.shape({
    dueDays: PropTypes.arrayOf(PropTypes.string),
    dueTime: PropTypes.string,
    timeZone: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  daysOfWeek: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  timeZoneOptions: PropTypes.array,
  parentApp: PropTypes.string,
  isSubmitting: PropTypes.bool,
  /** Optional course start/end ISO dates used to bound the schedule date range. */
  courseDateBounds: PropTypes.shape({
    courseStartDate: PropTypes.string,
    courseEndDate: PropTypes.string
  }),
  callback: PropTypes.shape({
    onBack: PropTypes.func,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
    onDueDaysChange: PropTypes.func,
    onDueTimeChange: PropTypes.func,
    onTimeZoneChange: PropTypes.func,
    onStartDateChange: PropTypes.func,
    onEndDateChange: PropTypes.func
  })
};

QuickScheduleForm.defaultProps = {
  initialValues: {},
  daysOfWeek: [],
  timeZoneOptions: [],
  parentApp: 'aet',
  isSubmitting: false,
  courseDateBounds: {},
  callback: {}
};

export default QuickScheduleForm;
